/*
=========================================================
* RingFoods Stress Shield Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// @mui icons
import ShieldIcon from "@mui/icons-material/Shield";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { accentSuccess, accentError, brandPrimary, grey100 } from "pages/RingFoodsHomepage/theme";

function StressShield() {
  const keyMessages = [
    {
      label: "Angry caller?",
      highlight: "Handled.",
      description: "RingFoods responds with patience and empathy every time. No raised voices, no shaken staff, no one dreading their next shift.",
    },
    {
      label: "Language barrier?",
      highlight: "Gone.",
      description: "Auto-detects the caller's language and responds natively. Mandarin, Spanish, French, Arabic -- every caller feels welcome.",
    },
    {
      label: "2AM call?",
      highlight: "Answered.",
      description: "Your phone rings at 2am, RingFoods picks up. Reservation booked, question answered, and you never lost a minute of sleep.",
    },
    {
      label: "Rush hour phone?",
      highlight: "Silenced.",
      description: "Your host greets guests, your servers serve tables, and the phone handles itself. No more choosing who gets ignored.",
    },
  ];

  const beforeAfter = [
    { before: "Staff dreads the phone ringing", after: "Staff never touches the phone" },
    { before: "Hostile callers ruin shifts", after: "AI absorbs every complaint with calm" },
    { before: "Owner wakes up at 2am", after: "Owner sleeps through the night" },
    { before: "Language barriers lose customers", after: "Every caller served in their language" },
    { before: "FAQ calls waste hours daily", after: "Common questions answered instantly" },
    { before: "Missed calls become bad reviews", after: "Every call answered on the first ring" },
  ];

  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      sx={{
        backgroundImage: {
          xs: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url(https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=50&fm=webp)`,
          md: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url(https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200&q=60&fm=webp)`,
        },
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: { md: "fixed", xs: "scroll" },
      }}
    >
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <ShieldIcon sx={{ color: accentSuccess, fontSize: 36, mr: 1.5 }} />
              <MKTypography
                variant="h2"
                fontWeight="bold"
                color="white"
                sx={{ fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
              >
                What If the Phone Was Never Your Problem Again?
              </MKTypography>
            </MKBox>
            <MKTypography variant="body1" color="white" sx={{ fontSize: { xs: "1rem", md: "1.125rem" }, opacity: 0.9 }}>
              The AI answers your restaurant&apos;s calls &mdash; the good, the bad, and the 2am
              ones &mdash; so your team never has to.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: "stretch" }}>
          {/* Left: Key Messages */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <MKBox sx={{ display: "flex", flexDirection: "column", width: "100%", justifyContent: "space-between" }}>
              {keyMessages.map((msg, index) => (
                <MKBox
                  key={index}
                  sx={{
                    flex: 1,
                    mb: index < keyMessages.length - 1 ? 2 : 0,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <MKTypography variant="h5" color="white" fontWeight={700} sx={{ mb: 1, fontSize: { xs: "1.15rem", md: "1.35rem" } }}>
                    {msg.label}{" "}
                    <MKTypography
                      component="span"
                      sx={{
                        fontSize: "inherit",
                        fontWeight: 700,
                        color: accentSuccess,
                      }}
                    >
                      {msg.highlight}
                    </MKTypography>
                  </MKTypography>
                  <MKTypography variant="body2" color="white" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
                    {msg.description}
                  </MKTypography>
                </MKBox>
              ))}
            </MKBox>
          </Grid>

          {/* Right: Before/After */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <MKBox sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <MKTypography variant="h4" color="white" fontWeight={700} sx={{ mb: 2.5, textAlign: "center", fontSize: { xs: "1.35rem", md: "1.65rem" } }}>
                Before &amp; After RingFoods
              </MKTypography>

              {/* Column Headers */}
              <MKBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  px: { xs: 1.5, md: 2 },
                }}
              >
                <MKBox sx={{ flex: 1 }}>
                  <MKTypography variant="overline" color="white" sx={{ fontWeight: 800, letterSpacing: 2, fontSize: { xs: "0.75rem", md: "0.85rem" } }}>
                    BEFORE
                  </MKTypography>
                </MKBox>
                <MKBox sx={{ width: { xs: 28, md: 36 } }} />
                <MKBox sx={{ flex: 1 }}>
                  <MKTypography variant="overline" color="white" sx={{ fontWeight: 800, letterSpacing: 2, fontSize: { xs: "0.75rem", md: "0.85rem" } }}>
                    AFTER
                  </MKTypography>
                </MKBox>
              </MKBox>

              {/* Rows - flex: 1 so they fill remaining height evenly */}
              <MKBox sx={{ display: "flex", flexDirection: "column", flex: 1, justifyContent: "space-between" }}>
                {beforeAfter.map((row, index) => (
                  <MKBox
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      flex: 1,
                      mb: index < beforeAfter.length - 1 ? 1.25 : 0,
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    {/* Before */}
                    <MKBox
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        p: { xs: 1.5, md: 2 },
                        backgroundColor: "rgba(239, 68, 68, 0.15)",
                        borderLeft: `3px solid ${accentError}`,
                        borderRadius: "8px 0 0 8px",
                      }}
                    >
                      <CloseIcon sx={{ color: "#FFFFFF", fontSize: { xs: 15, md: 18 }, mr: 1, flexShrink: 0 }} />
                      <MKTypography
                        variant="body2"
                        color="white"
                        sx={{
                          fontSize: { xs: "0.85rem", md: "0.95rem" },
                          lineHeight: 1.4,
                          textDecoration: "line-through",
                          textDecorationColor: "rgba(255, 255, 255, 0.5)",
                        }}
                      >
                        {row.before}
                      </MKTypography>
                    </MKBox>

                    {/* Arrow */}
                    <MKBox
                      sx={{
                        width: { xs: 28, md: 36 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: { xs: 18, md: 22 }, color: accentSuccess }} />
                    </MKBox>

                    {/* After */}
                    <MKBox
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        p: { xs: 1.5, md: 2 },
                        backgroundColor: "rgba(34, 197, 94, 0.18)",
                        borderRight: `3px solid ${accentSuccess}`,
                        borderRadius: "0 8px 8px 0",
                      }}
                    >
                      <CheckIcon sx={{ color: "#FFFFFF", fontSize: { xs: 15, md: 18 }, mr: 1, flexShrink: 0 }} />
                      <MKTypography
                        variant="body2"
                        color="white"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.85rem", md: "0.95rem" },
                          lineHeight: 1.4,
                        }}
                      >
                        {row.after}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                ))}
              </MKBox>
            </MKBox>
          </Grid>
        </Grid>

        {/* Bottom Tagline */}
        <Grid container spacing={3} sx={{ mt: { xs: 4, md: 6 } }}>
          <Grid item xs={12} textAlign="center">
            <MKBox
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                border: "1px solid rgba(34, 197, 94, 0.3)",
              }}
            >
              <MKTypography
                variant="h4"
                color="white"
                fontWeight={700}
                sx={{ fontSize: { xs: "1.25rem", md: "1.75rem" } }}
              >
                RingFoods isn&apos;t a phone bot. It&apos;s a stress shield for your entire restaurant.
              </MKTypography>
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default StressShield;
